// Renders a single ticket_message body + attachments cluster.
//
// What it cleans up vs. raw rendering:
//   1. `[cid:image001.png@01DCDC94...]` placeholders — Outlook's inline-image
//      markers in the plain-text alternative. We try to match each one to an
//      attachment by filename and inline the image; otherwise strip cleanly.
//   2. `[signature_3732896606]` and similar Outlook signature placeholders —
//      drop entirely; they're meaningless tokens.
//   3. URLs in plain text — wrapped in clickable <a> tags so the team can
//      jump to the referenced content without copy-paste.
//   4. Attachments — surfaces them visually under the body. Images render as
//      a thumbnail row; non-images render as a "📎 filename · size" chip
//      that opens the public URL in a new tab.
//
// Stays non-destructive: the original body text in the DB is untouched.
// This is purely a presentation layer — workflow 01's stored body and any
// future re-fetch will work the same.

import { useMemo, useState, type ReactNode } from 'react';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';
import { Paperclip, Download } from 'lucide-react';
import { useRetryAttachment } from '@/lib/queries';
import { normalizeOutboundHtml } from '@/lib/html-normalize';
import { useLightbox, handleHtmlImageClick } from '@/components/Lightbox';

interface Attachment {
  filename?: string;
  contentType?: string;
  size_bytes?: number;
  url?: string | null;
  // Some inbound messages carry a content_id explicitly when we ever start
  // tracking it server-side. Currently optional / future-proofing.
  content_id?: string | null;
}

interface Props {
  body: string;
  /** Optional HTML body (migration 028). When present, rendered (sanitized) in
   * preference to the plain-text body — preserves inline images, formatting,
   * tables, etc. Workflow 01 pre-resolves cid: refs to public URLs; this
   * component additionally handles any leftover refs as a client-side fallback. */
  bodyHtml?: string | null;
  attachments: unknown; // jsonb — can be array, null, or unexpected shape
  /** The ticket_messages.id this body belongs to. Needed by the failed-
   *  attachment chips so their Retry button knows which row to patch. */
  messageId?: string;
  /** Tweaks to spacing — outbound messages already have a tinted background. */
  variant?: 'inbound' | 'outbound';
  /** Render `blob:` images. Only for a reply that has not been sent yet. */
  allowBlobImages?: boolean;
}

// Cheap email-body-content URL matcher. Matches http(s)://… up to the next
// whitespace / closing bracket / quote / paren. Same shape as workflow 01's
// linkify pattern so plain text and re-rendering stay consistent.
const URL_RE = /(https?:\/\/[^\s<>"')\]]+)/g;

// Outlook signature placeholders: `[signature_XXXXX]`, `[signature]`. Drop.
const SIG_PLACEHOLDER_RE = /\[signature[^\]]*\]\s*/gi;

// Outlook inline-image markers: `[cid:image001.png@01DCDC94.1E8A4180]`.
// We need to keep the cid value to attempt matching against attachments.
const CID_PLACEHOLDER_RE = /\[cid:([^\]]+)\]/gi;

function parseAttachments(raw: unknown): Attachment[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (a): a is Attachment => a !== null && typeof a === 'object',
  );
}

function isImage(att: Attachment): boolean {
  return typeof att.contentType === 'string' && att.contentType.startsWith('image/');
}

function humanSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

// Try to find an attachment that matches the cid value. Email cids look like
// `image001.png@01DCDC94.1E8A4180` — the leading filename usually matches an
// attached file's name. We do a case-insensitive match on the filename
// portion.
function matchCidToAttachment(cid: string, attachments: Attachment[]): Attachment | null {
  const filenamePart = cid.split('@')[0]?.toLowerCase().trim();
  if (!filenamePart) return null;
  return (
    attachments.find(
      (a) => typeof a.filename === 'string' && a.filename.toLowerCase() === filenamePart,
    ) ?? null
  );
}

interface Segment {
  kind: 'text' | 'url' | 'cid_image' | 'cid_dropped';
  value: string;
  /** When kind === 'cid_image', the resolved attachment URL. */
  imageUrl?: string;
  /** When kind === 'cid_image', the alt text. */
  imageAlt?: string;
}

// Split the body into segments preserving order. Order of replacement:
//   1. Strip signature placeholders entirely (no segment produced).
//   2. Replace cid: placeholders with image segments (if matched) or drop.
//   3. Match URLs across the remaining text.
function splitBody(body: string, attachments: Attachment[]): Segment[] {
  // Step 1: remove signature placeholders.
  let working = body.replace(SIG_PLACEHOLDER_RE, '');

  // Step 2: tokenize cid placeholders.
  // We replace each cid placeholder with a unique sentinel, then split on it
  // so we get text/cid/text segments interleaved.
  const cids: { cid: string; resolved: Attachment | null }[] = [];
  working = working.replace(CID_PLACEHOLDER_RE, (_match, cid: string) => {
    const resolved = matchCidToAttachment(cid, attachments);
    cids.push({ cid: cid.trim(), resolved });
    return ` CID${cids.length - 1} `;
  });

  const segments: Segment[] = [];
  const cidSplit = working.split(/ CID(\d+) /);
  // After split, indices 0,2,4,... are text; 1,3,5,... are the cid index strings.
  for (let i = 0; i < cidSplit.length; i++) {
    const part = cidSplit[i];
    if (i % 2 === 0) {
      // Text segment — apply URL linkification.
      if (part.length > 0) {
        for (const sub of splitText(part)) {
          segments.push(sub);
        }
      }
    } else {
      // CID segment.
      const idx = Number(part);
      const entry = cids[idx];
      if (!entry) continue;
      if (entry.resolved && entry.resolved.url) {
        segments.push({
          kind: 'cid_image',
          value: entry.cid,
          imageUrl: entry.resolved.url,
          imageAlt: entry.resolved.filename ?? 'inline image',
        });
      } else {
        // No matching attachment — silently drop the placeholder so the
        // reader doesn't see noise.
        segments.push({ kind: 'cid_dropped', value: '' });
      }
    }
  }
  return segments;
}

function splitText(text: string): Segment[] {
  const out: Segment[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    if (m.index > last) out.push({ kind: 'text', value: text.slice(last, m.index) });
    out.push({ kind: 'url', value: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ kind: 'text', value: text.slice(last) });
  return out;
}

// Resolve any leftover `cid:xxx` image references in the HTML to attachment
// URLs (workflow 01 pre-resolves on insert, but this is a belt-and-braces
// fallback for legacy rows or edge cases where the workflow didn't have the
// cid info at insert time).
//
// Also handles two upstream realities the workflow can't always catch:
//   * Apple Mail "rich link" previews (e.g. Loom share URLs pasted into Mail)
//     embed the preview thumbnail as `<img src="EF4DEAEC-25C6-4576-99CC-…">`
//     — a bare content-id UUID with no `cid:` prefix. Match these against
//     attachment content_ids; strip the tag entirely if no match (otherwise
//     the browser renders a broken-image icon).
//   * Some clients leave `<img src="">` after stripping inline data. Those
//     also collapse to a broken icon — drop them.
function resolveCidsClientSide(
  html: string,
  attachments: Attachment[],
  allowBlob = false,
): string {
  if (!html) return '';

  // Pass 1: explicit `cid:xxx` references — match by content_id or by filename.
  let working = html.replace(
    /(src\s*=\s*["']?)cid:([^"'\s>)]+)(["']?)/gi,
    (_full, pre: string, cid: string, post: string) => {
      const lower = cid.toLowerCase();
      const byCid = attachments.find(
        (a) =>
          (typeof a.content_id === 'string' && a.content_id.toLowerCase() === lower) ||
          (typeof a.content_id === 'string' &&
            a.content_id.toLowerCase().replace(/^<|>$/g, '') === lower),
      );
      if (byCid?.url) return pre + byCid.url + post;
      // Fallback: match by filename embedded in the cid (e.g. "image001.png@…")
      const filenamePart = cid.split('@')[0]?.toLowerCase().trim();
      const byName = attachments.find(
        (a) =>
          typeof a.filename === 'string' &&
          a.filename.toLowerCase() === filenamePart,
      );
      if (byName?.url) return pre + byName.url + post;
      return _full;
    },
  );

  // Pass 2: walk every <img> and check that its src is a usable URL. Anything
  // else (bare UUID, empty string, "?", relative path with no host) gets
  // stripped — both the tag and any wrapping <a> link end up not rendering a
  // broken icon.
  working = working.replace(/<img\b[^>]*>/gi, (tag) => {
    const srcMatch = tag.match(/\bsrc\s*=\s*["']([^"']*)["']/i);
    const src = srcMatch?.[1]?.trim() ?? '';
    if (isUsableImgSrc(src, allowBlob)) return tag;
    // Try one more rescue: if the bare value looks like a UUID, search
    // attachments for a matching content_id (Apple Mail rich-link case).
    if (/^[A-F0-9]{8}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{4}-[A-F0-9]{12}$/i.test(src)) {
      const lower = src.toLowerCase();
      const byCid = attachments.find(
        (a) =>
          typeof a.content_id === 'string' &&
          a.content_id.toLowerCase().replace(/^<|>$/g, '') === lower,
      );
      if (byCid?.url) {
        return tag.replace(
          /\bsrc\s*=\s*["'][^"']*["']/i,
          `src="${byCid.url}"`,
        );
      }
    }
    return ''; // strip — broken refs don't earn a slot in the rendered email.
  });

  return working;
}

function isUsableImgSrc(src: string, allowBlob = false): boolean {
  if (!src) return false;
  // A reply still waiting to be sent holds its pasted images as blob: URLs.
  if (allowBlob && src.startsWith('blob:')) return true;
  if (src.startsWith('https://') || src.startsWith('http://')) return true;
  if (src.startsWith('data:image/')) return true;
  if (src.startsWith('//')) return true; // protocol-relative
  if (src.startsWith('/')) return true; // root-relative (rare in email but harmless)
  return false;
}

// DOMPurify config — allow rich email content (tables, inline styles, images,
// links) but strip anything that could execute or phone home. We deliberately
// allow `<style>` blocks because email signatures rely on them; DOMPurify
// scopes them safely. data: URIs allowed for images so legacy embedded
// images render; http(s) URLs covered by the default ALLOWED_URI_REGEXP.
const PURIFY_CONFIG: Parameters<typeof DOMPurify.sanitize>[1] = {
  ADD_ATTR: ['target', 'rel'],
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'meta'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  // Keep <style> for email-CSS, but DOMPurify isolates them.
  ALLOW_DATA_ATTR: false,
};

// A reply that is still waiting to be sent holds its pasted screenshots as
// `blob:` URLs. DOMPurify's default URI check strips those, so the pending
// bubble (components/PendingReplies.tsx) opts in. A blob: URL only resolves in
// the document that created it, so this cannot load anything from a message.
const PURIFY_CONFIG_WITH_BLOBS: Parameters<typeof DOMPurify.sanitize>[1] = {
  ...PURIFY_CONFIG,
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|cid|blob):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
};

export function MessageBody({
  body,
  bodyHtml,
  attachments,
  messageId,
  variant,
  allowBlobImages,
}: Props) {
  const atts = parseAttachments(attachments);
  const lightbox = useLightbox();

  // HTML render path — strongly preferred when present.
  const sanitizedHtml = useMemo(() => {
    if (!bodyHtml || bodyHtml.trim().length === 0) return null;
    // Normalise OUR OWN outbound HTML — strips the empty <div><br></div>
    // spacers + flat-divs-to-<p>s that Chrome's contentEditable produces.
    // Covers stored rows from BEFORE the send-time normalize landed (the
    // composer now does this on Send, but old replies in the DB still
    // carry the messy markup). Inbound is left alone — client formatting
    // varies wildly and a regex pass would risk breaking legit layouts.
    const normalised = variant === 'outbound' ? normalizeOutboundHtml(bodyHtml) : bodyHtml;
    const resolved = resolveCidsClientSide(normalised, atts, allowBlobImages);
    return DOMPurify.sanitize(resolved, allowBlobImages ? PURIFY_CONFIG_WITH_BLOBS : PURIFY_CONFIG);
  }, [bodyHtml, atts, variant, allowBlobImages]);

  // If we have a sanitized HTML body, render it directly. Attachments not
  // already inlined inside the HTML still show in the trailing cluster.
  if (sanitizedHtml) {
    // Build the set of URLs the sanitized HTML references inline so we don't
    // double-show them in the trailing cluster.
    const inlinedUrls = new Set<string>();
    for (const a of atts) {
      if (a.url && sanitizedHtml.includes(a.url)) inlinedUrls.add(a.url);
    }
    // Trailing cluster: anything with a URL (and not already shown inline),
    // PLUS anything with `url=null` (workflow 01 saw the attachment but the
    // upload failed). The null-url branch renders a "couldn't download"
    // warning chip so the team knows something is there to grab manually
    // from Gmail — never silent.
    const trailing = atts.filter((a) => a.url && !inlinedUrls.has(a.url));
    const failed = atts.filter((a) => !a.url);
    return (
      <div className="flex flex-col gap-3">
        <div
          // Explicit per-element styling because Tailwind Typography
          // isn't installed in this project — `prose` would be a no-op.
          // Tailwind's preflight strips browser defaults for <p> margins,
          // <ul>/<ol> bullets + indents, etc., so without these arbitrary
          // selectors a bold-italic-bulleted reply renders as a single
          // collapsed run of plain text. Each `[&_<tag>]:…` reinstates
          // the formatting our agents (and clients) actually expect to
          // see in a rich email thread.
          className={[
            'email-body-html max-w-none break-words text-sm leading-relaxed text-foreground/90',
            // Block-level spacing — restores paragraph air.
            '[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
            // Inline emphasis — browser defaults usually carry these
            // through, but we set them explicitly so consumers who reset
            // font-weight via prose-style libs still see the contrast.
            '[&_strong]:font-semibold [&_b]:font-semibold [&_em]:italic [&_i]:italic [&_u]:underline',
            // Lists — Tailwind preflight removes the marker + indent.
            '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-0.5',
            // Block quotes — visible left rule + muted text colour.
            '[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground',
            // Headings — small but distinct sizes so an agent who used
            // h2/h3 (e.g. a long template with sections) still scans well.
            '[&_h1]:mb-2 [&_h1]:mt-3 [&_h1]:text-base [&_h1]:font-semibold',
            '[&_h2]:mb-1.5 [&_h2]:mt-2.5 [&_h2]:text-base [&_h2]:font-semibold',
            '[&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-semibold',
            // Inline code + code blocks.
            '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs',
            '[&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-2 [&_pre]:font-mono [&_pre]:text-xs',
            // Existing accent for links, images, tables.
            '[&_a]:text-tint-info-fg [&_a]:underline-offset-2 [&_a:hover]:underline',
            '[&_img]:max-w-full [&_img]:rounded [&_img]:border [&_img]:border-border/50',
            '[&_table]:max-w-full [&_table]:border-collapse',
            // Horizontal rule — useful for templated emails with sections.
            '[&_hr]:my-3 [&_hr]:border-border/70',

            // ── Dark mode ─────────────────────────────────────────────────
            // Email HTML carries the sender's own inline styles, authored
            // against a white background — `style="color:#0f172a"` and the
            // like — so in dark mode any text WITH an inline colour stays dark
            // and disappears, while text without one inherits ours and shows.
            // Half a message visible is worse than none.
            //
            // The light surface that solves this lives on the whole <article>
            // in ticket-detail.tsx, NOT here. An earlier version put a white
            // card around just this body, which left a hard white block
            // floating inside a dark bubble — an obvious seam. The message is
            // the sheet; this element is transparent and inherits it.
            //
            // What DOES belong here: the element styles above use theme
            // tokens, which resolve to LIGHT values in dark mode and would be
            // invisible against that sheet. Re-point them at fixed light-mode
            // colours.
            'dark:text-slate-900',
            'dark:[&_a]:text-blue-700',
            'dark:[&_blockquote]:border-slate-300 dark:[&_blockquote]:text-slate-600',
            'dark:[&_code]:bg-slate-100 dark:[&_code]:text-slate-900',
            'dark:[&_pre]:bg-slate-100 dark:[&_pre]:text-slate-900',
            'dark:[&_img]:border-slate-200',
            'dark:[&_hr]:border-slate-300',
            'dark:[&_th]:border-slate-300 dark:[&_td]:border-slate-300',
          ].join(' ')}
          // Sanitized: DOMPurify above stripped scripts, event handlers, iframes, etc.
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          // Event delegation for inline <img> clicks — anywhere inside
          // the rendered email body, clicking an image (or an anchor
          // that wraps one) pops the lightbox instead of opening a new
          // tab. See Lightbox.tsx → handleHtmlImageClick for the
          // detection logic; non-image clicks fall through untouched
          // (text-only links keep their normal target="_blank" nav).
          onClick={(e) => handleHtmlImageClick(e, lightbox.open)}
        />
        {trailing.length > 0 ? <AttachmentCluster attachments={trailing} /> : null}
        {failed.length > 0 ? <FailedAttachmentCluster attachments={failed} messageId={messageId} originalAttachments={atts} /> : null}
      </div>
    );
  }

  // Fallback: plain-text body with cid placeholder + URL linkification.
  const segments = splitBody(body ?? '', atts);

  // Set of attachment URLs already rendered inline (via cid match) so we
  // don't double-render them in the attachments cluster below.
  const inlinedUrls = new Set<string>();
  for (const s of segments) {
    if (s.kind === 'cid_image' && s.imageUrl) inlinedUrls.add(s.imageUrl);
  }

  const renderedBody: ReactNode[] = segments.map((s, i) => {
    if (s.kind === 'text') return <span key={i}>{s.value}</span>;
    if (s.kind === 'url') {
      return (
        <a
          key={i}
          href={s.value}
          target="_blank"
          rel="noopener noreferrer"
          className="break-all text-tint-info-fg underline-offset-2 hover:underline"
        >
          {s.value}
        </a>
      );
    }
    if (s.kind === 'cid_image' && s.imageUrl) {
      return (
        <button
          key={i}
          type="button"
          onClick={() => lightbox.open(s.imageUrl!, s.imageAlt ?? 'Inline image', s.imageAlt)}
          className="my-1 inline-block max-w-full"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={s.imageUrl}
            alt={s.imageAlt ?? 'inline image'}
            className="max-h-80 max-w-full rounded border border-border/50 align-middle"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        </button>
      );
    }
    // cid_dropped — render nothing
    return null;
  });

  // Attachments not already inlined as cid:
  const trailingAttachments = atts.filter((a) => a.url && !inlinedUrls.has(a.url));
  const failedAttachments = atts.filter((a) => !a.url);

  return (
    <div className="flex flex-col gap-3">
      <div className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-foreground/90 dark:text-slate-900">
        {renderedBody}
      </div>
      {trailingAttachments.length > 0 ? <AttachmentCluster attachments={trailingAttachments} /> : null}
      {failedAttachments.length > 0 ? <FailedAttachmentCluster attachments={failedAttachments} messageId={messageId} originalAttachments={atts} /> : null}
    </div>
  );
}

// Map a filename extension to a (colour-tinted, type-labelled) chip.
// Picking a small palette of recognisable groupings — sheets/docs/PDFs/
// archives/code/images — covers ~95% of real-world support attachments
// and lets the eye categorise without reading the filename. Falls back
// to a neutral slate "FILE" chip for unknowns.
type FileKind = {
  /** Lower-cased label drawn inside the chip. */
  label: string;
  /** Tailwind class group for the chip background + text. */
  className: string;
  /** Friendly noun shown in the meta row ("CSV file", "PDF document"). */
  noun: string;
};
const FILE_KINDS: Record<string, FileKind> = {
  pdf:  { label: 'pdf',  className: 'bg-tint-critical text-tint-critical-fg ring-tint-critical-ring',         noun: 'PDF document' },
  csv:  { label: 'csv',  className: 'bg-tint-success text-tint-success-fg ring-tint-success-ring', noun: 'CSV file' },
  xls:  { label: 'xls',  className: 'bg-tint-success text-tint-success-fg ring-tint-success-ring', noun: 'Spreadsheet' },
  xlsx: { label: 'xlsx', className: 'bg-tint-success text-tint-success-fg ring-tint-success-ring', noun: 'Spreadsheet' },
  doc:  { label: 'doc',  className: 'bg-tint-info text-tint-info-fg ring-tint-info-ring',           noun: 'Word document' },
  docx: { label: 'docx', className: 'bg-tint-info text-tint-info-fg ring-tint-info-ring',           noun: 'Word document' },
  txt:  { label: 'txt',  className: 'bg-tint-neutral text-tint-neutral-fg ring-tint-neutral-ring',        noun: 'Text file' },
  zip:  { label: 'zip',  className: 'bg-tint-warning text-tint-warning-fg ring-tint-warning-ring',        noun: 'Archive' },
  rar:  { label: 'rar',  className: 'bg-tint-warning text-tint-warning-fg ring-tint-warning-ring',        noun: 'Archive' },
  '7z': { label: '7z',   className: 'bg-tint-warning text-tint-warning-fg ring-tint-warning-ring',        noun: 'Archive' },
  json: { label: 'json', className: 'bg-tint-active text-tint-active-fg ring-tint-active-ring',     noun: 'JSON data' },
  xml:  { label: 'xml',  className: 'bg-tint-active text-tint-active-fg ring-tint-active-ring',     noun: 'XML data' },
  html: { label: 'html', className: 'bg-tint-high text-tint-high-fg ring-tint-high-ring',     noun: 'HTML page' },
  mp3:  { label: 'mp3',  className: 'bg-tint-pending text-tint-pending-fg ring-tint-pending-ring',  noun: 'Audio' },
  mp4:  { label: 'mp4',  className: 'bg-tint-pending text-tint-pending-fg ring-tint-pending-ring',  noun: 'Video' },
  mov:  { label: 'mov',  className: 'bg-tint-pending text-tint-pending-fg ring-tint-pending-ring',  noun: 'Video' },
};
const FILE_KIND_FALLBACK: FileKind = {
  label: 'file',
  className: 'bg-tint-neutral text-tint-neutral-fg ring-tint-neutral-ring',
  noun: 'File',
};
function fileKindOf(filename: string): FileKind {
  const ext = filename.toLowerCase().split('.').pop() ?? '';
  return FILE_KINDS[ext] ?? FILE_KIND_FALLBACK;
}

// Trailing cluster shown below the body. Used by both the HTML and plain-
// text render paths.
//
// Refreshed (2026-05) so attachments stand out and don't get missed:
//   - The whole cluster lives in a tinted card with an explicit
//     "Attachments (N)" header (was: untitled flex row that visually
//     merged into the body).
//   - Non-image files get a colourful, type-labelled square (CSV =
//     emerald, PDF = rose, doc = blue, archive = amber, …) so the eye
//     can categorise without reading the filename.
//   - Each row shows the filename in bold + a meta line ("CSV file ·
//     204.5 KB"), with a Download icon at the right as an explicit
//     affordance.
//   - Images keep their thumbnail row, just sized down to 80px to live
//     inside the same card without dominating the rest of the message.
function AttachmentCluster({ attachments }: { attachments: Attachment[] }) {
  const lightbox = useLightbox();
  const images = attachments.filter(isImage);
  const files = attachments.filter((a) => !isImage(a));
  const total = attachments.length;

  return (
    <section className="overflow-hidden rounded-md border border-tint-warning-ring bg-tint-warning/60 dark:border-amber-300 dark:bg-amber-50">
      <header className="flex items-center gap-1.5 border-b border-tint-warning-ring/60 bg-tint-warning/40 px-3 py-1.5 dark:border-amber-300/60 dark:bg-amber-100/70">
        <Paperclip className="h-3.5 w-3.5 text-tint-warning-fg dark:text-amber-800" aria-hidden />
        <span className="text-2xs font-semibold uppercase tracking-wide text-tint-warning-fg dark:text-amber-800">
          {total} attachment{total === 1 ? '' : 's'}
        </span>
      </header>

      {/* Image thumbnails row (if any). Click → pop the lightbox. */}
      {images.length > 0 ? (
        <div className="flex flex-wrap gap-2 border-b border-tint-warning-ring/40 p-3">
          {images.map((a, i) => {
            const size = humanSize(a.size_bytes);
            const filename = a.filename ?? 'image';
            return (
              <button
                key={`img-${i}`}
                type="button"
                onClick={() => a.url && lightbox.open(a.url, filename, filename)}
                disabled={!a.url}
                className="inline-block"
                title={`${filename}${size ? ' · ' + size : ''}`}
              >
                <img
                  src={a.url ?? ''}
                  alt={filename}
                  className="h-20 w-20 rounded border border-tint-warning-ring/70 object-cover transition-opacity hover:opacity-90"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Non-image files — one row each, full-width clickable. */}
      {files.length > 0 ? (
        <ul className="divide-y divide-tint-warning-ring/40">
          {files.map((a, i) => {
            const filename = a.filename ?? 'file';
            const kind = fileKindOf(filename);
            const size = humanSize(a.size_bytes);
            return (
              <li key={`file-${i}`}>
                <a
                  href={a.url ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 px-3 py-2 hover:bg-tint-warning/40 dark:hover:bg-amber-100"
                  title={filename}
                >
                  <span
                    className={`grid h-9 w-9 shrink-0 place-items-center rounded-md text-3xs font-bold uppercase ring-1 ring-inset ${kind.className}`}
                    aria-hidden
                  >
                    {kind.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground dark:text-slate-900">{filename}</p>
                    <p className="truncate text-2xs text-muted-foreground dark:text-slate-600">
                      {kind.noun}
                      {size ? ` · ${size}` : ''}
                    </p>
                  </div>
                  <Download
                    className="h-4 w-4 shrink-0 text-tint-warning-fg/70 transition-colors group-hover:text-tint-warning-fg dark:text-amber-800 dark:group-hover:text-amber-950"
                    aria-hidden
                  />
                </a>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}

// Trailing cluster for attachments where workflow 01 saw the file but the
// upload step failed (`url === null` in the jsonb). We never hide these —
// the user needs to know something arrived in Gmail that we couldn't pull
// across, so they can grab it manually if it matters.
function FailedAttachmentCluster({
  attachments,
  messageId,
  originalAttachments,
}: {
  attachments: Attachment[];
  /** ticket_messages.id — needed by the Retry button. Without it the
   *  button is hidden and the user falls back to the manual workflow. */
  messageId?: string;
  /** Full attachments array as it appears on the row, so we can map a
   *  null-url entry back to its index when calling retry-attachment. */
  originalAttachments: Attachment[];
}) {
  return (
    <div className="flex flex-col gap-2 border-t border-tint-warning-ring/50 bg-tint-warning/40 px-3 py-2 dark:border-amber-300/60 dark:bg-amber-100/70">
      <p className="flex items-center gap-1.5 text-3xs font-semibold uppercase tracking-wide text-tint-warning-fg dark:text-amber-800">
        <span aria-hidden>⚠</span>
        Attachment couldn't be downloaded ({attachments.length})
      </p>
      <ul className="flex flex-wrap gap-2">
        {attachments.map((a) => {
          // Match each failed attachment back to its index in the
          // original array so retry-attachment knows which row slot to
          // patch. Identity match (===) works because both lists came
          // from the same parseAttachments() call.
          const idx = originalAttachments.indexOf(a);
          return (
            <FailedAttachmentChip
              key={idx}
              attachment={a}
              messageId={messageId}
              attachmentIndex={idx}
            />
          );
        })}
      </ul>
      <p className="text-3xs text-tint-warning-fg/80 dark:text-amber-900">
        Click <strong>Retry</strong> to re-fetch from Gmail, or open the original
        email and grab the file manually.
      </p>
    </div>
  );
}

function FailedAttachmentChip({
  attachment: a,
  messageId,
  attachmentIndex,
}: {
  attachment: Attachment;
  messageId?: string;
  attachmentIndex: number;
}) {
  const retry = useRetryAttachment();
  const [retried, setRetried] = useState(false);
  // canRetry: we know the row id + the slot index, so retry-attachment can
  // act. Without messageId we hide the button (parent didn't pass it —
  // shouldn't happen in production but defensive).
  const canRetry = !!messageId && attachmentIndex >= 0 && !retried;

  function handleRetry() {
    if (!canRetry || !messageId) return;
    retry.mutate(
      { message_id: messageId, attachment_index: attachmentIndex },
      {
        onSuccess: (res) => {
          if (res.url) {
            setRetried(true);
            toast.success(`Recovered ${res.filename ?? 'attachment'}`);
          }
        },
        onError: (err) =>
          toast.error(
            err instanceof Error
              ? `Retry failed: ${err.message}`
              : 'Retry failed',
          ),
      },
    );
  }

  return (
    <li className="inline-flex items-center gap-1.5 rounded-md border border-tint-warning-ring/60 bg-background px-2 py-1 text-xs text-tint-warning-fg dark:border-amber-300 dark:bg-white dark:text-amber-900">
      <span aria-hidden>📎</span>
      <span className="max-w-[16rem] truncate font-medium">{a.filename ?? 'file'}</span>
      {a.size_bytes ? (
        <span className="text-muted-foreground dark:text-slate-600">· {humanSize(a.size_bytes)}</span>
      ) : null}
      {canRetry ? (
        <button
          type="button"
          onClick={handleRetry}
          disabled={retry.isPending}
          className="ml-1 inline-flex h-5 items-center gap-0.5 rounded border border-tint-warning-fg/70 bg-tint-warning px-1.5 text-3xs font-medium text-tint-warning-fg transition-colors hover:bg-tint-warning-ring disabled:opacity-60"
          title="Re-fetch this attachment from Gmail"
        >
          {retry.isPending ? 'Retrying…' : 'Retry'}
        </button>
      ) : null}
    </li>
  );
}
